import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useOutletContext } from 'react-router-dom';
import { ClipboardCheck, X } from 'lucide-react';

import {
  factoryOrderAttentionHint,
  factoryOrderCreatedLabel,
  factoryOrderFactoryStepLabel,
  factoryOrderFilterBucket,
  factoryOrderOwnerLabel,
  factoryOrderReleaseUi,
  factoryOrderSecondaryNote,
  factoryOrderStatusLabel,
  factoryService,
  isFactoryBatchOwnerAccepted,
  isFactoryOrderShelfOpen,
  type FactoryOrder,
  type FactoryOrderListFilter,
} from '../../services/factoryService';
import type { FactoryDeskOutletContext } from '../deskOutlet';
import { useFactoryOrderScope } from '../orderScope';
import { useAuth } from '../../context/AuthContext';

const FILTERS: { key: FactoryOrderListFilter; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'active', label: '进行中' },
  { key: 'done', label: '已完成' },
  { key: 'cancelled', label: '已取消' },
];

const VISIT_DEPOSIT_FEN = 3990;

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  unpaid: '未支付',
  paying: '支付中',
  paid: '定金已付',
  settled: '已结算',
  refunded: '已退款',
};

function fenYuan(fen?: number | null): string {
  const n = Number(fen || 0) / 100;
  return `¥${(Math.round(n * 100) / 100).toFixed(2)}`;
}

function asText(value: unknown): string {
  return String(value || '').trim();
}

function asTextList(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => asText(item)).filter(Boolean) : [];
}

function claimStatsRows(order: FactoryOrder): { label: string; value: string }[] {
  const intake = order.intake && typeof order.intake === 'object'
    ? (order.intake as Record<string, unknown>)
    : {};
  const pages = Number(intake.pageCount || 0);
  const rate = Number(intake.tokenRateYuan || 0);
  const depositFen = Number(order.depositFen || intake.depositFen || intake.estimatedFeeFen || 0);
  const visitFen = Number(intake.visitDepositFen || 0) || (depositFen ? VISIT_DEPOSIT_FEN : 0);
  const paidFen = Number(order.paidFen || 0);
  const addonFen = visitFen > 0 ? Math.max(0, depositFen - visitFen) : 0;
  const location = [
    ...asTextList(intake.region),
    asText(intake.street),
    asText(intake.address),
  ].filter(Boolean).join(' ');
  const services = asTextList(intake.services);
  const photos = Array.isArray(intake.photos) ? intake.photos.length : 0;
  const payment = PAYMENT_STATUS_LABEL[String(order.paymentStatus || '')] || asText(order.paymentStatus);
  const addonDetail = pages && rate
    ? `${fenYuan(addonFen)} · ${pages} 页 × ¥${rate}/百页`
    : addonFen
      ? fenYuan(addonFen)
      : '';

  return [
    { label: '订单码', value: order.orderCode || '' },
    { label: '订单主', value: factoryOrderOwnerLabel(order) },
    { label: '服务', value: services.join('、') || asText(intake.intent) },
    { label: '预估页数', value: pages > 0 ? `${pages} 页` : '' },
    { label: '上门', value: visitFen > 0 ? fenYuan(visitFen) : '' },
    { label: '加收', value: addonDetail },
    { label: '定金', value: depositFen
      ? `${fenYuan(depositFen)}${paidFen ? ` · 已付 ${fenYuan(paidFen)}` : ''}`
      : '' },
    { label: '支付', value: payment },
    { label: '联系人', value: asText(intake.contactName) },
    { label: '电话', value: asText(intake.phone) },
    { label: '地址', value: location },
    { label: '现场照片', value: photos > 0 ? `${photos} 张` : '' },
    { label: '备注', value: asText(order.note) },
    { label: '下单时间', value: factoryOrderCreatedLabel(order) },
  ].filter((row) => row.value);
}

function factoryDeskOrderStatusLine(order: FactoryOrder): string {
  const hint = factoryOrderAttentionHint(order);
  if (hint) return hint;
  const bucket = factoryOrderFilterBucket(order);
  if (bucket === 'active') return factoryOrderFactoryStepLabel(order);
  return factoryOrderStatusLabel[order.status] || order.status;
}

export function FactoryOrdersPage() {
  const { user } = useAuth();
  const { refreshKey = 0 } = useOutletContext<FactoryDeskOutletContext>() ?? { refreshKey: 0 };
  const { activeOrderId, setActiveOrderId } = useFactoryOrderScope();
  const [orders, setOrders] = useState<FactoryOrder[]>([]);
  const [filter, setFilter] = useState<FactoryOrderListFilter>('active');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [claimOrder, setClaimOrder] = useState<FactoryOrder | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setOrders(await factoryService.listFactoryOrders());
  }, []);

  const filterCounts = useMemo(() => {
    const counts: Record<FactoryOrderListFilter, number> = {
      all: orders.length,
      active: 0,
      done: 0,
      cancelled: 0,
    };
    for (const order of orders) {
      counts[factoryOrderFilterBucket(order)] += 1;
    }
    return counts;
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (filter === 'all') return orders;
    return orders.filter((o) => factoryOrderFilterBucket(o) === filter);
  }, [filter, orders]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        if (refreshKey > 0) setLoading(true);
        await reload();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload, refreshKey]);

  async function onConfirmClaim() {
    if (!claimOrder) return;
    const orderId = claimOrder.id;
    setBusyId(orderId);
    setError(null);
    try {
      await factoryService.claimOrder(orderId);
      setActiveOrderId(orderId);
      setClaimOrder(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function onAbandon(orderId: string) {
    setBusyId(orderId);
    setError(null);
    try {
      await factoryService.abandonOrder(orderId);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-ink">订单</h1>
        <p className="mt-1 text-sm text-muted">
          学习侧订单主下发的工厂订单。点条目设为当前订单后，源货架 / 任务 / 成品智能体 / 释放只看这一单，避免流程混淆。
        </p>
      </header>

      {error && !claimOrder ? <p className="text-sm text-danger">{error}</p> : null}

      <section className="rounded-md border border-[#3a4450] bg-[#1e242c] p-4">
        <div className="mb-3 flex flex-wrap gap-2">
          {FILTERS.map((item) => {
            const active = filter === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setFilter(item.key)}
                className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                  active
                    ? 'border-[#2EC4B6]/50 bg-[#2EC4B6]/15 text-[#2EC4B6]'
                    : 'border-[#3a4450] text-[#9AA3B0] hover:border-[#2EC4B6]/40 hover:text-[#E8ECF1]'
                }`}
              >
                {item.label} {filterCounts[item.key]}
              </button>
            );
          })}
        </div>

        {loading ? <p className="text-sm text-muted">加载中…</p> : null}
        {!loading && orders.length === 0 ? (
          <p className="text-sm text-muted">暂无订单。请订单主在学习壳「工厂订单」发布。</p>
        ) : null}
        {!loading && orders.length > 0 && filteredOrders.length === 0 ? (
          <p className="text-sm text-muted">当前筛选下没有订单。</p>
        ) : null}
        <ul className="divide-y divide-[#3a4450]">
          {filteredOrders.map((o) => {
            const isMine = o.assigneeUserId === user?.id;
            const secondaryNote = factoryOrderSecondaryNote(o);
            const releaseUi = factoryOrderReleaseUi(o);
            const accepted = releaseUi
              ? isFactoryBatchOwnerAccepted(releaseUi.batch)
              : (o.batches || []).some((b) => isFactoryBatchOwnerAccepted(b));
            const statusHint = factoryOrderAttentionHint(o);
            const statusLine = factoryDeskOrderStatusLine(o);
            const releaseMode = releaseUi?.mode;
            return (
              <li
                key={o.id}
                className={`flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg px-3 py-3 ${
                  activeOrderId === o.id ? 'bg-[#2EC4B6]/10' : ''
                }`}
              >
                <button
                  type="button"
                  onClick={() => setActiveOrderId(o.id)}
                  className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-5 gap-y-1 text-left"
                >
                  <span className="shrink-0 font-medium text-ink">
                    订单主 {factoryOrderOwnerLabel(o)}
                  </span>
                  <span className="font-mono text-sm text-[#2EC4B6]">
                    {o.orderCode || '—'}
                  </span>
                  <span
                    className={
                      statusHint === '颗粒释放中' || releaseMode === 'releasing'
                        ? 'text-sm font-medium text-success'
                        : statusHint === '颗粒待释放' || releaseMode === 'awaiting'
                          ? 'text-sm font-medium text-[#E8B86D]'
                          : 'text-sm text-muted'
                    }
                  >
                    {statusLine}
                  </span>
                  {factoryOrderCreatedLabel(o) ? (
                    <span className="text-sm text-[#8B94A3]">{factoryOrderCreatedLabel(o)}</span>
                  ) : null}
                  {activeOrderId === o.id ? (
                    <span className="text-xs font-medium text-[#2EC4B6]">当前订单</span>
                  ) : (
                    <span className="text-xs text-[#8B94A3]">点此切换</span>
                  )}
                  {secondaryNote ? (
                    <span className="basis-full text-xs text-[#8B94A3]">{secondaryNote}</span>
                  ) : null}
                </button>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {accepted ? (
                    <span className="factory-accept-stamp factory-accept-stamp--static" aria-label="已验收">
                      已验收
                    </span>
                  ) : null}
                  {o.status === 'open' ? (
                    <button
                      type="button"
                      disabled={busyId === o.id}
                      onClick={() => {
                        setError(null);
                        setClaimOrder(o);
                      }}
                      className="inline-flex items-center gap-1 rounded-md bg-[#2EC4B6] px-3 py-1.5 text-sm font-semibold text-[#15191f] disabled:opacity-50"
                    >
                      <ClipboardCheck size={14} />
                      {busyId === o.id ? '接单中…' : '去接单'}
                    </button>
                  ) : null}
                  {isMine && isFactoryOrderShelfOpen(o) ? (
                    <Link
                      to="/factory/shelf"
                      onClick={() => setActiveOrderId(o.id)}
                      className="rounded-md border border-[#3a4450] px-3 py-1.5 text-sm text-[#2EC4B6]"
                    >
                      去上架
                    </Link>
                  ) : null}
                  {o.status === 'claimed' && isMine ? (
                    <button
                      type="button"
                      disabled={busyId === o.id}
                      onClick={() => void onAbandon(o.id)}
                      className="rounded-md border border-[#3a4450] px-3 py-1.5 text-sm text-[#F07178] disabled:opacity-50"
                    >
                      {busyId === o.id ? '放弃中…' : '放弃订单'}
                    </button>
                  ) : null}
                  {releaseUi && isMine ? (
                    <Link
                      to="/factory/release"
                      onClick={() => setActiveOrderId(o.id)}
                      className="rounded-md border border-[#3a4450] px-3 py-1.5 text-sm text-[#2EC4B6]"
                    >
                      去释放
                    </Link>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {claimOrder
        ? createPortal(
            <div
              className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4"
              role="presentation"
              onClick={(e) => {
                if (e.target === e.currentTarget && busyId !== claimOrder.id) {
                  setClaimOrder(null);
                }
              }}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="factory-claim-title"
                className="max-h-[85dvh] w-full max-w-md overflow-y-auto rounded-2xl border border-[#3a4450] bg-[#1e242c] p-5 shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 id="factory-claim-title" className="text-lg font-semibold text-[#F2F5F8]">
                      确认接单
                    </h3>
                    <p className="mt-1 text-sm text-[#9AA3B0]">
                      请核对订单统计后再接。同时只能接 1 单。
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setClaimOrder(null)}
                    disabled={busyId === claimOrder.id}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#3a4450] text-[#9AA3B0] hover:text-[#E8ECF1] disabled:opacity-50"
                    aria-label="关闭"
                  >
                    <X size={16} />
                  </button>
                </div>
                <dl className="mt-4 space-y-2 text-sm">
                  {claimStatsRows(claimOrder).map((row) => (
                    <div key={row.label} className="flex items-baseline justify-between gap-3">
                      <dt className="shrink-0 text-[#9AA3B0]">{row.label}</dt>
                      <dd className="min-w-0 break-words text-right text-[#E8ECF1]">{row.value}</dd>
                    </div>
                  ))}
                </dl>
                {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    disabled={busyId === claimOrder.id}
                    onClick={() => setClaimOrder(null)}
                    className="rounded-lg border border-[#3a4450] px-4 py-2 text-sm text-[#C5CDD8] hover:text-[#E8ECF1] disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    disabled={busyId === claimOrder.id}
                    onClick={() => void onConfirmClaim()}
                    className="rounded-lg bg-[#2EC4B6] px-4 py-2 text-sm font-semibold text-[#15191f] disabled:opacity-50"
                  >
                    {busyId === claimOrder.id ? '接单中…' : '确认接单'}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
